import { FormInput } from '@common/components/forms';
import { SuspenseButton } from '@common/components/utility';
import { anyPromiseTrackerTagsInProgress } from '@common/utils';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

export interface ISuspenseConfirmModalProps {
  visible: boolean;
  size?: 'sm' | 'lg' | 'xl' | undefined;
  tags: string[];
  hideSpinner?: boolean;
  title: string;
  description: React.ReactNode | string | JSX.Element;
  subDescription?: React.ReactNode | string | JSX.Element;
  checkboxLabel?: string | null; // Label for the checkbox
  confirmButtonText?: string;
  cancelButtonText?: string;
  onConfirm: (isConfirmed?: boolean) => void;
  onCancel: () => void;
}

/**
 * Yes/No Confirmation Model with built in support for multiple promise tags to watch for
 */
export const SuspenseConfirmationModal = observer(
  ({
    visible,
    title,
    size,
    description,
    subDescription,
    checkboxLabel,
    tags,
    hideSpinner,
    confirmButtonText = 'Yes',
    cancelButtonText = 'Cancel',
    onConfirm,
    onCancel,
  }: ISuspenseConfirmModalProps) => {
    React.useEffect(() => {
      if (visible) {
        const el = document.getElementsByClassName('fade modal-backdrop show');
        requestAnimationFrame(() => {
          if (el.length > 0 && (el[1] as HTMLElement)?.style) {
            (el[1] as HTMLElement).style.zIndex = '1070';
          }
        });
      }
    }, [visible]);

    const { control, watch, reset } = useForm({
      defaultValues: { confirmationCheckbox: false },
    }); // Initialize React Hook Form
    const isConfirmed = watch('confirmationCheckbox', false); // Watch checkbox state
    // Pass checkbox value to the onConfirm callback
    const handleConfirm = () => {
      onConfirm(isConfirmed); // Pass the checkbox state as an argument
    };
    // Handle form reset when modal visibility changes
    useEffect(() => {
      if (!visible) {
        reset({ confirmationCheckbox: false }); // Reset the form values when modal is closed
      }
    }, [visible, reset]);
    return (
      <Modal
        style={{ zIndex: 1071 }}
        centered
        className="confirm-dialog test"
        data-testid="confirm-dialog"
        show={visible}
        onHide={onCancel}
        size={size}
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mt-3">{description}</p>
          {subDescription && <p className="mt-3 fw-bold">{subDescription}</p>}
          {checkboxLabel && (
            <div className="mt-3 mx-1 confirmationCheckBox ">
              <FormInput
                type="checkbox"
                fieldName="confirmationCheckbox"
                controlId="confirmation-checkbox"
                formGroupClassName="d-flex"
                label={checkboxLabel}
                control={control}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="container-fluid">
          <Button
            variant="light"
            className={classNames('cancel', 'text-uppercase')}
            disabled={anyPromiseTrackerTagsInProgress(tags)}
            onClick={onCancel}
          >
            {cancelButtonText}
          </Button>
          <SuspenseButton
            tags={tags}
            variant="primary"
            className={classNames('confirm', 'text-uppercase')}
            hideSpinner={hideSpinner}
            onClick={handleConfirm}
          >
            {confirmButtonText}
          </SuspenseButton>
        </Modal.Footer>
      </Modal>
    );
  },
);
